import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { getVideoUrl, getPosterUrl } from '../../config/assets';

interface HeroSlide {
  id: string;
  videoUrl: string;
  mobileVideoUrl?: string; // Optional mobile video
  posterUrl: string; // Static poster image
  mobilePosterUrl?: string; // Optional mobile poster
  headline: React.ReactNode; // Changed to ReactNode to support JSX
  subheadline?: string;
  ctaText?: string;
  ctaLink?: string;
}

// Configure your hero slides here
// Videos are served from Hetzner S3 via CDN for optimal performance
// Dynamic URLs are generated based on environment configuration
const heroSlides: HeroSlide[] = [
  {
    id: 'discover',
    videoUrl: getVideoUrl('discover.mp4'),
    mobileVideoUrl: getVideoUrl('discover-mobile.mp4'),
    posterUrl: getPosterUrl('discover.jpg'),
    mobilePosterUrl: getPosterUrl('discover-mobile.jpg'),
    headline: <>Let customers <strong>discover</strong> your products</>,
    subheadline: 'They\'ll feel guided, understood, and delighted.',
    ctaText: 'Try it on your site',
    ctaLink: '/signup'
  },
  {
    id: 'understand',
    videoUrl: getVideoUrl('understand.mp4'),
    mobileVideoUrl: getVideoUrl('understand-mobile.mp4'),
    posterUrl: getPosterUrl('understand.jpg'),
    mobilePosterUrl: getPosterUrl('understand-mobile.jpg'),
    headline: <>Let visitors <strong>understand</strong> your services</>,
    subheadline: 'Complex offerings become crystal clear.',
    ctaText: 'See how it works',
    ctaLink: '/features'
  },
  {
    id: 'fall-in-love',
    videoUrl: getVideoUrl('fall-in-love.mp4'),
    mobileVideoUrl: getVideoUrl('fall-in-love-mobile.mp4'),
    posterUrl: getPosterUrl('fall-in-love.jpg'),
    mobilePosterUrl: getPosterUrl('fall-in-love-mobile.jpg'),
    headline: <>Let them <strong>fall in love</strong> with your brand</>,
    subheadline: 'Create memorable experiences that convert.',
    ctaText: 'Get started',
    ctaLink: '/signup'
  },
  {
    id: 'curiosity',
    videoUrl: getVideoUrl('curiosity.mp4'),
    mobileVideoUrl: getVideoUrl('curiosity-mobile.mp4'),
    posterUrl: getPosterUrl('curiosity.jpg'),
    mobilePosterUrl: getPosterUrl('curiosity-mobile.jpg'),
    headline: <>Let curiosity <strong>lead to conversion</strong></>,
    subheadline: 'Answer questions before they\'re asked.',
    ctaText: 'Learn more',
    ctaLink: '/features'
  },
  {
    id: 'value',
    videoUrl: getVideoUrl('value.mp4'),
    mobileVideoUrl: getVideoUrl('value-mobile.mp4'),
    posterUrl: getPosterUrl('value.jpg'),
    mobilePosterUrl: getPosterUrl('value-mobile.jpg'),
    headline: <>Let your <strong>value</strong> be obvious</>,
    subheadline: 'No more missed opportunities.',
    ctaText: 'Start free trial',
    ctaLink: '/signup'
  },
  {
    id: 'feel-at-home',
    videoUrl: getVideoUrl('feel-at-home.mp4'),
    mobileVideoUrl: getVideoUrl('feel-at-home-mobile.mp4'),
    posterUrl: getPosterUrl('feel-at-home.jpg'),
    mobilePosterUrl: getPosterUrl('feel-at-home-mobile.jpg'),
    headline: <>Let visitors <strong>feel at home</strong></>,
    subheadline: 'Personalized experiences for everyone.',
    ctaText: 'Try PageMind',
    ctaLink: '/signup'
  }
];

export function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videosLoaded, setVideosLoaded] = useState<{ [key: string]: boolean }>({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const mobileVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const navigate = useNavigate();

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Preload all videos
    heroSlides.forEach((slide, index) => {
      if (videoRefs.current[index]) {
        videoRefs.current[index]!.load();
      }
      if (mobileVideoRefs.current[index] && slide.mobileVideoUrl) {
        mobileVideoRefs.current[index]!.load();
      }
    });
  }, []);

  // Mark videos as loaded when they start playing
  useEffect(() => {
    const currentSlideData = heroSlides[currentSlide];
    const posterKey = `${currentSlideData.id}-${isMobile ? 'mobile' : 'desktop'}`;
    
    const currentVideoRef = isMobile ? mobileVideoRefs : videoRefs;
    const currentVideo = currentVideoRef.current[currentSlide];
    
    if (currentVideo) {
      const handleCanPlay = () => {
        setVideosLoaded(prev => ({ ...prev, [posterKey]: true }));
        // Mark initial load as complete after first video loads
        if (!initialLoadComplete) {
          setInitialLoadComplete(true);
        }
      };
      
      currentVideo.addEventListener('canplay', handleCanPlay);
      return () => currentVideo.removeEventListener('canplay', handleCanPlay);
    }
  }, [currentSlide, isMobile, initialLoadComplete]);

  useEffect(() => {
    // Start playing current video (mobile or desktop)
    const currentVideoRef = isMobile ? mobileVideoRefs : videoRefs;
    const currentVideo = currentVideoRef.current[currentSlide];
    
    if (currentVideo) {
      currentVideo.currentTime = 0;
      currentVideo.play().catch(err => {
        // Silently handle autoplay failure - browser power saving mode
        // Video will play when user interacts with page
      });
    }

    // Set up timer for 8 seconds (matching video length)
    const timer = setTimeout(() => {
      handleSlideChange();
    }, 8000);

    return () => clearTimeout(timer);
  }, [currentSlide, isMobile]);

  const handleSlideChange = () => {
    setIsTransitioning(true);
    
    // Pause current video (both mobile and desktop)
    const desktopVideo = videoRefs.current[currentSlide];
    const mobileVideo = mobileVideoRefs.current[currentSlide];
    
    if (desktopVideo) desktopVideo.pause();
    if (mobileVideo) mobileVideo.pause();

    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setIsTransitioning(false);
    }, 300);
  };

  const handleCtaClick = (link?: string) => {
    if (link) {
      // Special handling for signup links - redirect to onboarding signup
      if (link === '/signup') {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const signupUrl = isLocalhost 
          ? 'http://localhost:3000/signup' 
          : 'https://onboarding.pagemind.ai/signup';
        window.location.href = signupUrl;
      } else {
        // For other links, use React Router navigation
        navigate(link);
      }
    }
  };

  return (
    <section className="relative w-full h-screen overflow-hidden bg-black">
      {/* Video backgrounds */}
      {heroSlides.map((slide, index) => {
        const posterKey = `${slide.id}-${isMobile ? 'mobile' : 'desktop'}`;
        const isVideoLoaded = videosLoaded[posterKey];
        const posterUrl = isMobile && slide.mobilePosterUrl ? slide.mobilePosterUrl : slide.posterUrl;
        
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentSlide && !isTransitioning ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Poster image (first frame) - only shown during initial load, not during playback */}
            {posterUrl && !initialLoadComplete && !isVideoLoaded && (
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${posterUrl})` }}
              />
            )}
            
            {/* Desktop video */}
            <video
              ref={(el) => (videoRefs.current[index] = el)}
              className={`absolute inset-0 w-full h-full object-cover ${isMobile ? 'hidden' : 'block'}`}
              src={slide.videoUrl}
              muted
              playsInline
              loop={false}
              preload="auto"
            />
            
            {/* Mobile video */}
            {slide.mobileVideoUrl && (
              <video
                ref={(el) => (mobileVideoRefs.current[index] = el)}
                className={`absolute inset-0 w-full h-full object-cover ${isMobile ? 'block' : 'hidden'}`}
                src={slide.mobileVideoUrl}
                muted
                playsInline
                loop={false}
                preload="auto"
              />
            )}
            
            {/* Fallback to desktop video if no mobile version */}
            {!slide.mobileVideoUrl && (
              <video
                className={`absolute inset-0 w-full h-full object-cover ${isMobile ? 'block' : 'hidden'}`}
                src={slide.videoUrl}
                muted
                playsInline
                loop={false}
                preload="auto"
              />
            )}
            
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black/40" />
          </div>
        );
      })}

      {/* Content overlay */}
      <div className="relative z-10 flex items-center justify-center h-full px-4">
        <div className="text-center max-w-5xl mx-auto">
          {/* Animated text */}
          <div
            className={`transition-all duration-700 transform ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 drop-shadow-lg px-4">
              {heroSlides[currentSlide].headline}
            </h1>
            
            {heroSlides[currentSlide].subheadline && (
              <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-6 md:mb-8 drop-shadow-md px-4">
                {heroSlides[currentSlide].subheadline}
              </p>
            )}
            
            {heroSlides[currentSlide].ctaText && (
              <Button
                size="lg"
                onClick={() => handleCtaClick(heroSlides[currentSlide].ctaLink)}
                className="bg-white text-black hover:bg-white/90 text-lg px-8 py-6 shadow-xl"
              >
                {heroSlides[currentSlide].ctaText}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Skip button for accessibility */}
      <button
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        className="absolute bottom-8 right-8 text-white/70 hover:text-white transition-colors z-20"
      >
        <svg className="w-6 h-6 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </button>
    </section>
  );
}